<template>
  <AuthView title="Fix Form">
    <v-form :ref="setFormRef" @submit.prevent="submitForm">
      <AppRow>
        <TextField v-model.lazy="formId" label="Form ID" :rules="[required]" />
        <TextField v-model.lazy="applicantId" label="Applicant ID" :rules="[required]" />
        <EmailField v-model="email" label="Email" />
      </AppRow>
      <AppRow>
        <AppSelect v-model="source" :items="sourceOptions" label="Source" :rules="[required]" />
        <VisaCountrySelect v-model="country" :rules="[required]" />
      </AppRow>
      <AppRow>
        <AppMultiSelect
          v-for="category in categories"
          v-model="selectedIssues[category.key]"
          :items="category.issues"
          :label="category.label + ' Issues'"
          item-title="label"
          class="mb-4"
          multiple small-chips
        />
      </AppRow>
      <AppRow class="mt-4">
        <AppBtn primary :loading type="submit">
          Submit
        </AppBtn>
      </AppRow>
      <AppRow class="mt-4">
        <AppAlert v-if="submitted && issuesError" color="error" class="mb-4">
          {{ issuesError }}
        </AppAlert>
      </AppRow>
    </v-form>
  </AuthView>
</template>

<script setup lang="ts">
import { sendFixRequestEmail } from '~/helpers/http';
import { getIssueCategoriesForCountry, getFixIssuesByCategory } from '~/helpers/fix-issues';
import { VISA_COUNTRY_KEYS } from '~/assets/constants';
import type { FixRequestSource, CountryKey, SelectItem, FixIssueCategory, FixIssue } from '~/types';
import { objectKeys } from '~/helpers/general';

const { setFormRef, loading, submit, reset, submitted } = useForm();
const { required } = useValidation();
const query = useRoute().query as Record<string, string>;

const formId = ref(query.form || '');
const applicantId = ref(query.applicant || '');
const email = ref(query.email || '');
const country = ref(query.country as CountryKey || VISA_COUNTRY_KEYS[0]);

const categories = computed(() => getIssueCategoriesForCountry(country.value));
const selectedIssues = reactive<Record<FixIssueCategory['key'], FixIssue['value'][]>>({
  face_photo: [],
  passport_photo: [],
  business: [],
  passport_expiry: [],
  application_details: []
});
const source = ref<FixRequestSource>('form');

const sourceOptions: SelectItem<FixRequestSource>[] = [
  { value: 'form', title: 'Form' },
  { value: 'authorities', title: 'Authorities' }
];

watch(categories, (newCategories) => {
  objectKeys(selectedIssues).forEach(key => delete selectedIssues[key]);
  newCategories.forEach(category => {
    selectedIssues[category.key] = [];
  });
});

function getAllSelectedIssues() {
  return Object.values(selectedIssues).flat();
}

const issuesError = ref('');

function submitForm() {
  issuesError.value = '';
  submit(async () => {
    if (!getAllSelectedIssues().length) {
      issuesError.value = 'Please select at least one issue.';
      return;
    }
    const response = await sendFixRequestEmail({
      formId: formId.value,
      applicantId: applicantId.value,
      email: email.value,
      source: source.value,
      issues: getFixIssuesByCategory(selectedIssues, categories.value)
    }).catch(() => null);

    if (!response?.success) {
      throw new Error('Failed to submit issues');
    }
    reset();
    issuesError.value = '';
  }, { successMsg: 'Issues submitted successfully' });
}
</script>